/**
 * Quão velho está o dado publicado — funções puras (testadas em
 * `frescor.test.ts`).
 *
 * Mesma ideia do módulo de estações, com limiares próprios: o pipeline das
 * embarcações roda dentro do ciclo da chuva, a cada 15 minutos, mas a posição
 * de um navio envelhece mais rápido que um acumulado de chuva. Um navio a 10
 * nós anda 9 km em meia hora — depois disso a seta no mapa está apontando
 * para onde ele já não está.
 */

/** Cadência nominal do pipeline (o passo `navios.mjs` do workflow da chuva). */
export const CADENCIA_MIN = 15;

/** Dois ciclos perdidos: a posição já não serve como "agora". */
export const ATRASO_AVISO_MIN = CADENCIA_MIN * 2;

/** Seis horas: a frota inteira mudou, o que está no mapa é história. */
export const ATRASO_GRAVE_MIN = 6 * 60;

export type Frescor = 'ok' | 'atrasado' | 'velho' | 'desconhecido';

/** Minutos desde a publicação; `null` quando não dá para saber. */
export function atrasoMin(gerado: number | null, agora: number = Date.now()): number | null {
  if (gerado === null || !Number.isFinite(gerado)) return null;
  // Relógio do visitante adiantado não deve virar "atraso negativo".
  return Math.max(0, (agora - gerado) / 60_000);
}

export function frescorDe(gerado: number | null, agora: number = Date.now()): Frescor {
  const min = atrasoMin(gerado, agora);
  if (min === null) return 'desconhecido';
  if (min >= ATRASO_GRAVE_MIN) return 'velho';
  if (min >= ATRASO_AVISO_MIN) return 'atrasado';
  return 'ok';
}

/** Texto do aviso, ou `null` quando está tudo em dia. */
export function avisoDeFrescor(gerado: number | null, agora: number = Date.now()): string | null {
  const f = frescorDe(gerado, agora);
  if (f === 'ok' || f === 'desconhecido') return null;
  const min = atrasoMin(gerado, agora)!;
  const quanto = min >= 120 ? `${Math.floor(min / 60)} h` : `${Math.round(min)} min`;
  return f === 'velho'
    ? `Posições paradas há ${quanto}. O que está no mapa não é onde a frota está agora.`
    : `Posições sem atualizar há ${quanto} — o normal é a cada ${CADENCIA_MIN} min.`;
}
